import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Innsyn i tenantens TA-leverandør (TW 36436874) — felles for alle TA-er via provider-mønsteret.

export type TaOverviewStatus = 'Ok' | 'NotSupported' | 'NotConfigured' | 'Unavailable';
export type TaDeliveryMethodStatus = 'Ok' | 'NotToTa' | 'CarrierNotFound' | 'ProductNotFound' | 'ProductMissing';

export interface TaOverviewService {
    code: string;
    name: string;
    mappingCode: string | null;
}

export interface TaOverviewCell {
    mappingCode: string | null;
    additionalServices: TaOverviewService[];
}

export interface TaOverviewProductRow {
    code: string;
    name: string;
    /** Annen kode en leveringsmåte kan peke på (f.eks. nShift ProdCSID). */
    alternateCode: string | null;
    /** Nøkkel = avsender-Id. Mangler nøkkelen, har avsenderen ikke produktet. */
    cells: Record<string, TaOverviewCell>;
    deliveryMethodNos: number[];
}

export interface TaOverviewCarrier {
    id: string;
    name: string;
    mappingCodeByConsignor: Record<string, string | null>;
    products: TaOverviewProductRow[];
}

export interface TaOverviewConsignor {
    id: string;
    name: string;
    productCount: number;
    compassConsignorCodes: string[];
    isCompassDefault: boolean;
}

export interface TaOverviewDeliveryMethod {
    deliveryMethodNo: number;
    description: string | null;
    carrierId: string | null;
    carrierName: string | null;
    productCode: string | null;
    productName: string | null;
    status: TaDeliveryMethodStatus;
    availableAtConsignorIds: string[];
    missingAtMappedConsignorIds: string[];
}

export interface TaOverviewIssue {
    severity: 'error' | 'warning' | 'info';
    message: string;
}

export interface TaConfigurationOverviewData {
    status: TaOverviewStatus;
    message: string | null;
    softwareId: number;
    softwareName: string | null;
    accountReference: string | null;
    fetchedAt: string;
    /** False = TA-en har én avsender fra systeminnstillingene; avsendermapping brukes ikke. */
    usesConsignorMapping: boolean;
    summary: {
        hasMappingCodes: boolean;
        consignorCount: number;
        carrierCount: number;
        distinctProductCount: number;
        mappingCodesSet: number;
        mappingCodesTotal: number;
        deliveryMethodsToTa: number;
        deliveryMethodsWithErrors: number;
    };
    consignors: TaOverviewConsignor[];
    carriers: TaOverviewCarrier[];
    deliveryMethods: TaOverviewDeliveryMethod[];
    issues: TaOverviewIssue[];
}

class TaService {
    private baseUrl = `${API_BASE_URL}/ta`;

    async getConfiguration(): Promise<TaConfigurationOverviewData> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/configuration`);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }
}

export const taService = new TaService();
